import React, {useMemo} from 'react';
import {ItemType, ViewId} from './FileViewer';
import {FILE_TYPE} from './const';
import {ImageItem} from './FileViewer/ImageItem';
import {VideoItem} from './FileViewer/VideoItem';
import {getThumbSize} from './StudioDatasetViewer';
import {CountAdornment, LabelAdornment} from './Adornment';
import cn from 'classnames';
import {DatasetMetadataLabelItem} from '../../types/dataService/DatasetMetadata';
import {FileItemContainer, FileItemContainerProps} from './FileViewer/FileContainer';
import {THUMBNAIL_SIZE_LIST} from '../../components/DatasetEdit/const';
import {FileStyle} from '../../components/DataPrepare/FileViewer/FileViewer';
import './Renderer.scss';

export type RendererProps = Pick<ItemType, 'type' | 'selected'> &
  Pick<FileItemContainerProps, 'overlayElement'> & {
    item: ItemType;
    url?: string | undefined | null | File;
    view: ViewId;
    labels?: DatasetMetadataLabelItem[];
    className?: string;

    readOnly: boolean;
    isBusy?: boolean;
    isLoadingThumbnail?: boolean;

    onSelect?: (item: ItemType) => void;
    onClick?: (
      item: ItemType,
      target?: React.MouseEvent<HTMLDivElement, MouseEvent>
    ) => void;
  };

export type CountProps = {
  labelCount: number;
  predictionCount: number;
  total: number;
};
export type RendererClassificationProps = RendererProps & Pick<ItemType, 'label'>;
export type RendererDetectionProps = {
  getCount?: (binaryId: string) => CountProps | undefined;
} & RendererProps;

// Default and no label/adornment support
export const BinaryRenderer = ({
  type,
  url,
  selected = false,
  view,
  item,
  className = '',
  isBusy = false,
  overlayElement,
  isLoadingThumbnail = false,
  onSelect = () => {},
  onClick = () => {},
}: RendererProps) => {
  const thumbSize = getThumbSize(view, selected);
  const isImage = useMemo(() => type === FILE_TYPE.IMAGE, [type]);

  return (
    <FileItemContainer
      key={item.id}
      selected={selected}
      showSelection={true}
      overlayElement={overlayElement}
      className={`${className} default ${isImage ? 'image' : 'video'}`}
      onSelect={() => onSelect(item)}
      onClick={() => !isBusy && onClick(item)}
    >
      {isImage ? (
        <ImageItem url={url} height={thumbSize} width={thumbSize} isBusy={isBusy} />
      ) : (
        <VideoItem
          url={url}
          isBusy={isBusy}
          name={item.filename}
          isLoadingThumbnail={isLoadingThumbnail}
        />
      )}
    </FileItemContainer>
  );
};

export const BinaryListRenderer = ({url}: Pick<RendererClassificationProps, 'url'>) => {
  return <ImageItem url={url} height={THUMBNAIL_SIZE_LIST} width={THUMBNAIL_SIZE_LIST} />;
};

export type BinaryClassificationRendererProps = {
  getItemStyle?: (id: string) => FileStyle;
} & RendererClassificationProps;

export const BinaryClassificationRenderer = ({
  url,
  selected = false,
  view,
  item,
  onSelect = () => {},
  onClick = () => {},
  getItemStyle = () => undefined,
}: BinaryClassificationRendererProps) => {
  const thumbSize = getThumbSize(view, selected);
  return (
    <FileItemContainer
      key={item.id}
      selected={selected}
      onSelect={() => onSelect(item)}
      onClick={() => onClick(item)}
      showSelection={true}
      className="default"
    >
      <ImageItem
        url={url}
        height={thumbSize}
        width={thumbSize}
        adornment={<LabelAdornment label={item.label} style={getItemStyle(item.id)} />}
      />
    </FileItemContainer>
  );
};

export const BinaryDetectionRenderer = ({
  url,
  selected = false,
  view,
  labels,
  getCount = () => ({labelCount: 0, predictionCount: 0, total: 0}),
  item,
  onSelect = () => {},
  onClick = () => {},
}: RendererDetectionProps) => {
  const thumbSize = getThumbSize(view, selected);
  const count = getCount(item.id);
  return (
    <FileItemContainer
      key={item.id}
      selected={selected}
      onSelect={() => onSelect(item)}
      onClick={() => onClick(item)}
      showSelection={false}
      className="detection"
    >
      <ImageItem
        url={url}
        height={thumbSize}
        width={thumbSize}
        adornment={
          <CountAdornment
            className={cn({
              'count-adornment__prediction':
                count?.predictionCount && count?.labelCount === 0,
              'count-adornment__combo': count?.predictionCount && count?.labelCount,
            })}
            count={count?.total}
          />
        }
      />
    </FileItemContainer>
  );
};
